import { Breadcrumb, Button, Layout, Spin, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import IBroker from '~Api/Broker/IBroker';
import IHubspotBroker from '~Api/HubspotBroker/IHubspotBroker';
import { brokersHubspotErrorsSelector } from './selectors';
import { brokerHubspotErrorRetrySyncAction, brokersHubspotErrorsListAction } from './actions';
import { ExportOutlined } from '@ant-design/icons';
import './brokers.less';
import { IDictionary } from '~utilities/IDictionary';
import constants from '~constants';

export default function HubspotErrors(): ReactElement {
    const hubspotBrokers: IDictionary<IHubspotBroker> = useSelector(brokersHubspotErrorsSelector);

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!hubspotBrokers) {
            dispatch(brokersHubspotErrorsListAction());
        }
    }, [
        hubspotBrokers,
        dispatch,
    ]);

    const onClickRetrySync: (hubspotBroker: IHubspotBroker) => void = useCallback((hubspotBroker: IHubspotBroker) => {
        dispatch(brokerHubspotErrorRetrySyncAction(hubspotBroker.uuid));
    }, [
        dispatch,
    ]);

    if (!hubspotBrokers) {
        return (
            <Layout className='brokers'>
                <Breadcrumb className='breadcrumb'>
                    <Breadcrumb.Item>Home</Breadcrumb.Item>
                    <Breadcrumb.Item><Link to='/brokers'>Brokers</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>Hubspot Errors</Breadcrumb.Item>
                </Breadcrumb>
                <Layout className='content-wrapper'>
                    <Layout.Content className='content'>
                        <Typography.Title level={2}>Hubspot Errors</Typography.Title>
                        <Spin />
                    </Layout.Content>
                </Layout>
            </Layout>
        );
    }

    const columns: ColumnsType<IHubspotBroker> = [
        {
            render: (hubspotBroker: IHubspotBroker) => <Link to={`/brokers/${hubspotBroker.broker.uuid}`}>{hubspotBroker.broker.code}</Link>,
            title: 'Code',
            width: 150,
        },
        {
            dataIndex: 'broker',
            render: (broker: IBroker) => <Link to={`/brokers/${broker.uuid}`}>{broker.firstName} {broker.lastName}</Link>,
            title: 'Name',
            width: 300,
        },
        {
            dataIndex: 'contactId',
            render: (contactId: string) => contactId ? <a href={`https://app.hubspot.com/contacts/${constants.HUBSPOT_ACCOUNT_ID}/record/0-1/${contactId}`} rel='noreferrer' target='_blank'>{contactId} <ExportOutlined /></a> : '-',
            title: 'Hubspot ID',
            width: 150,
        },
        {
            dataIndex: 'errorMessage',
            render: (errorMessage: string) => errorMessage ?? '-',
            title: 'Error Message',
        },
        {
            dataIndex: 'modifiedTime',
            defaultSortOrder: 'descend',
            render: (modifiedTime: string) => dayjs(modifiedTime).format('D/M/YYYY HH:mm'),
            sorter: (a: IHubspotBroker, b: IHubspotBroker) => dayjs(a.modifiedTime) > dayjs(b.modifiedTime) ? 1 : -1,
            title: 'Date',
            width: 150,
        },
        {
            render: (hubspotBroker: IHubspotBroker): ReactElement => {
                const onClickRetry: () => void = () => onClickRetrySync(hubspotBroker);

                return (
                    <Button onClick={onClickRetry}>Retry</Button>
                );
            },
        },
    ];

    return (
        <Layout className='broker-hubspot-errors'>
            <Breadcrumb className='breadcrumb'>
                <Breadcrumb.Item>Home</Breadcrumb.Item>
                <Breadcrumb.Item><Link to='/brokers/list'>Brokers</Link></Breadcrumb.Item>
                <Breadcrumb.Item>Hubspot Errors</Breadcrumb.Item>
            </Breadcrumb>
            <Layout className='content-wrapper'>
                <Layout.Content className='content'>
                    <Typography.Title level={2}>Hubspot Errors</Typography.Title>
                    <Table
                        className='broker-hubspot-errors-list'
                        columns={columns}
                        dataSource={_.values(hubspotBrokers)}
                        pagination={false}
                        rowKey='uuid'
                        scroll={{ x: 1370 }}
                        size='middle'
                    />
                </Layout.Content>
            </Layout>
        </Layout>
    );
}
